import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ArrowDown } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const logoRef = useRef(null);
  const headlineRef = useRef(null);
  const subheadRef = useRef(null);
  const ctaRef = useRef(null);
  const [animationComplete, setAnimationComplete] = useState(false);

  const logoUrl = '/logo.png';

  useEffect(() => {
    const ctx = gsap.context(() => {
      const logo = logoRef.current;
      const tl = gsap.timeline();

      // Logo appears and spins
      tl.fromTo(logo, 
        { scale: 0, rotation: 0, opacity: 0 },
        { scale: 1.5, rotation: 720, opacity: 1, duration: 2, ease: 'power2.out' }
      )
      // Continue spinning briefly
      .to(logo, {
        rotation: 1080,
        duration: 0.8,
        ease: 'power1.inOut'
      })
      // Logo breaks and explodes into sprinkles
      .to(logo, {
        scale: 2,
        opacity: 0,
        duration: 0.6,
        ease: 'power2.in',
        onStart: () => {
          // Start creating sprinkles as logo breaks
          createBreakingSprinkles();
        },
        onComplete: () => {
          // Create massive sprinkle explosion
          createMassiveSprinkleExplosion();
          setAnimationComplete(true);
        }
      });

      // Content fade in after explosion
      tl.fromTo(headlineRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' },
        '-=0.5'
      )
      .fromTo(subheadRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' },
        '-=0.5'
      )
      .fromTo(ctaRef.current,
        { y: 30, opacity: 0, scale: 0.9 },
        { y: 0, opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(1.7)' },
        '-=0.5'
      );
    });

    return () => ctx.revert();
  }, []);

  const createBreakingSprinkles = () => {
    const colors = ['#ec4899', '#14b8a6', '#fbbf24', '#f97316', '#8b5cf6'];
    const container = document.getElementById('hero');
    
    // Create initial breaking sprinkles
    for (let i = 0; i < 40; i++) {
      const sprinkle = document.createElement('div');
      const size = 3 + Math.random() * 4;
      sprinkle.className = 'absolute rounded-full';
      sprinkle.style.width = size + 'px';
      sprinkle.style.height = size + 'px';
      sprinkle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      sprinkle.style.left = '50%';
      sprinkle.style.top = '40%';
      sprinkle.style.zIndex = '30';
      container.appendChild(sprinkle);

      const angle = (Math.PI * 2 * i) / 40;
      const distance = 80 + Math.random() * 120;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      gsap.to(sprinkle, {
        x: x,
        y: y,
        rotation: Math.random() * 360,
        opacity: 0.8,
        duration: 0.6,
        ease: 'power2.out'
      });
    }
  };

  const createMassiveSprinkleExplosion = () => {
    const colors = ['#ec4899', '#14b8a6', '#fbbf24', '#f97316', '#8b5cf6', '#ef4444'];
    const container = document.getElementById('hero');
    
    // Create 200+ sprinkles for massive explosion
    for (let i = 0; i < 200; i++) {
      const sprinkle = document.createElement('div');
      const size = 2 + Math.random() * 5;
      sprinkle.className = 'absolute rounded-full';
      sprinkle.style.width = size + 'px';
      sprinkle.style.height = size + 'px';
      sprinkle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      sprinkle.style.left = '50%';
      sprinkle.style.top = '40%';
      sprinkle.style.zIndex = '30';
      container.appendChild(sprinkle);

      const angle = (Math.PI * 2 * i) / 200;
      const distance = 150 + Math.random() * 500;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      const rotation = Math.random() * 1440;

      gsap.to(sprinkle, {
        x: x,
        y: y,
        rotation: rotation,
        opacity: 0,
        duration: 2 + Math.random() * 1.5,
        ease: 'power2.out',
        onComplete: () => sprinkle.remove()
      });
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('order-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-amber-50 via-pink-50 to-amber-100">
      {/* Spinning Logo that breaks into sprinkles */}
      <div ref={logoRef} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <img 
          src={logoUrl} 
          alt="Dough-nate Logo" 
          className="w-64 h-64 object-contain"
        />
      </div>

      {/* Hero Content */}
      <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
        <h1 ref={headlineRef} className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 opacity-0">
          A Sweeter Way to Give
        </h1>
        <p ref={subheadRef} className="text-xl md:text-2xl text-gray-700 mb-8 max-w-2xl mx-auto opacity-0">
          We supply premium doughnuts to charities, schools, and community groups, helping them raise funds for their own causes in a simple, ethical, and effective way.
        </p>
        <div ref={ctaRef} className="opacity-0">
          <Button
            onClick={scrollToForm}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg"
          >
            Partner With Us
            <ArrowDown className="ml-2" size={20} />
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      {animationComplete && (
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="text-amber-600" size={32} />
        </div>
      )}
    </section>
  );
};

export default Hero;